import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
  Chip,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderSpecial as FolderSpecialIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useComponentWebSocket } from './GlobalSettingUtils';
import { useFolderPicker } from '../useFolderPicker';
import AutoAddProjectDialog from './AutoAddProjectDialog';
import { fetchCollections } from '../../Utils/collectionsApi';

const CollectionSettings = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newName, setNewName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  
  const [autoAddDialogOpen, setAutoAddDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  const autoAddDialogRef = useRef();

  // Define colors for consistency
  const primaryColor = '#5757b0';
  const hoverColor = '#7777d0';

  // Command handlers; template reads are routed through HTTP.
  const commandHandlers = {
    'renameCollection': (message) => {
      if (message.data === 'success') {
        showAlert(t('collection.rename_success', 'Collection renamed successfully'), 'success');
        loadCollections(); // Refresh the list
        setEditingCollection(null);
        setNewName('');
      } else {
        const errorMessage = message.data?.message || t('collection.rename_error', 'Error renaming collection');
        showAlert(errorMessage, 'error');
      }
    },
    'deleteCollection': (message) => {
      if (message.data === 'success') {
        showAlert(t('collection.delete_success', 'Collection deleted successfully'), 'success');
        loadCollections(); // Refresh the list
        setDeleteDialogOpen(false);
        setCollectionToDelete(null);
      } else {
        const errorMessage = message.data?.message || t('collection.delete_error', 'Error deleting collection');
        showAlert(errorMessage, 'error');
      }
    },
    'getAllTemplates': (message) => {
      if (message.data && message.data.templates) {
        setTemplates(message.data.templates);
      } else if (message.templates) {
        setTemplates(message.templates);
      } else {
        setTemplates([]);
      }
      setTemplatesLoading(false);
    },
    'getAutoAddProjectConfig': (message) => {
      if (autoAddDialogRef.current && autoAddDialogRef.current.updateFormWithConfig) {
        const config = message.data && message.data.config ? message.data.config : {
          templateId: null,
          watchPath: '',
          enabled: false
        };
        autoAddDialogRef.current.updateFormWithConfig(config);
      }
    },
    'saveAutoAddProjectConfig': (message) => {
      if (message.data === 'success' || message.status === 'success') {
        showAlert(t('collection.auto_add_saved', 'Auto add project configuration saved successfully!'), 'success');
        setAutoAddDialogOpen(false);
        // Refresh collections to show updated auto project status
        loadCollections();
      } else {
        const errorMessage = message.data?.message || message.message || t('collection.auto_add_error', 'Error saving auto add project configuration');
        showAlert(errorMessage, 'error');
      }
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  // Folder picker for auto add project dialog
  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    onFolderSelected: (folderPath) => {
      // This will be handled by the dialog component (state lives inside)
    }
  });

  // Show alert messages
  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => {
      setAlert({ show: false, message: '', severity: 'success' });
    }, 4000);
  };

  // Load collections from server
  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchCollections();
      setCollections(Array.isArray(payload.collections) ? payload.collections : []);
    } catch (error) {
      console.error('Failed to load collections:', error);
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load collections on component mount
  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  // Handle edit collection
  const handleEditCollection = (collection) => {
    setEditingCollection(collection.id);
    setNewName(collection.title);
  };

  // Handle save edited collection
  const handleSaveEdit = () => {
    if (!newName.trim()) {
      showAlert(t('collection.name_required', 'Collection name is required'), 'error');
      return;
    }
    sendCommand('renameCollection', {
      collectionId: editingCollection,
      newName: newName.trim()
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingCollection(null);
    setNewName('');
  };

  // Handle delete collection
  const handleDeleteCollection = (collection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  // Confirm delete collection
  const confirmDeleteCollection = () => {
    if (collectionToDelete) {
      sendCommand('deleteCollection', {
        collectionId: collectionToDelete.id
      });
    }
  };

  // Handle auto create project
  const handleAutoCreateProject = (collection) => {
    setSelectedCollection(collection);
    setAutoAddDialogOpen(true);
    
    // Load templates if not already loaded
    if (templates.length === 0 && !templatesLoading) {
      setTemplatesLoading(true);
      sendCommand('getAllTemplates', {});
    }
  };

  // Expose methods to parent component if needed
  useImperativeHandle(ref, () => ({
    refresh: loadCollections
  }));

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <FolderSpecialIcon sx={{ fontSize: 32, color: '#5757b0' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5757b0' }}>
            {t('collection.manage_collections', 'Collection Management')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {t('collection.manage_description', 'View, rename, and delete your project collections. Collections help organize related projects together and can automatically create projects when new folders are detected.')}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: '#f8f9ff',
            border: '1px solid #e8eaff',
            borderRadius: 2,
            mb: 3,
            position: 'relative',
            zIndex: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#5757b0' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('collection.loading_collections', 'Loading collections...')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderSpecialIcon sx={{ fontSize: 16, color: '#5757b0' }} />
                  <Typography variant="body2" color="text.secondary">
                    {collections.length === 0
                      ? t('collection.no_collections_count', 'No collections found')
                      : collections.length === 1
                      ? t('collection.one_collection_found', '1 collection found')
                      : `${collections.length} ${t('collection.collections_found', 'collections found')}`
                    }
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={t('collection.refresh_collections_tooltip', 'Refresh collection list')}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadCollections}
                  disabled={loading}
                  sx={{
                    borderColor: '#5757b0',
                    color: '#5757b0',
                    '&:hover': {
                      borderColor: '#aaaaca',
                      backgroundColor: 'rgba(87, 87, 176, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {t('collection.refresh', 'Refresh')}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Alert Messages */}
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setAlert({ show: false, message: '', severity: 'success' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Collections List */}
      <Fade in={!loading} timeout={300}>
        <Box>
          {collections.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                backgroundColor: 'rgba(248, 249, 255, 0.6)',
                border: '2px dashed rgba(87, 87, 176, 0.2)',
                borderRadius: 3,
                backdropFilter: 'blur(10px)'
              }}
            >
              <FolderSpecialIcon sx={{ fontSize: 64, color: 'rgba(87, 87, 176, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: 'rgba(87, 87, 176, 0.7)' }}>
                {t('collection.no_collections', 'No collections found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('collection.no_collections_message', 'Create collections from the sidebar to organize your projects.')}
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(87, 87, 176, 0.15)',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
                position: 'relative',
                zIndex: 1,
                mt: 1
              }}
            >
              <List sx={{ p: 0, mt: 0 }}>
                {collections.map((collection, index) => (
                  <React.Fragment key={collection.id}>
                    <ListItem
                      sx={{
                        py: 1.75,
                        px: 2.5,
                        transition: 'box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.035)',
                          boxShadow: '0 6px 18px rgba(87,87,176,0.06)',
                          transform: 'translateY(-2px)'
                        },
                        position: 'relative'
                      }}
                    >
                      <ListItemIcon sx={{ mr: 2, minWidth: 'auto' }}>
                        <Box 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            backgroundColor: '#f8f9ff',
                            border: '1px solid #e8eaff',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FolderSpecialIcon sx={{ fontSize: 24, color: '#5757b0' }} />
                        </Box>
                      </ListItemIcon>

                      {editingCollection === collection.id ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <TextField
                            fullWidth
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            variant="outlined"
                            size="small"
                            placeholder={t('collection.collection_name', 'Collection name')}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                  borderColor: primaryColor
                                }
                              }
                            }}
                          />
                          <Tooltip title={t('common.save', 'Save')}>
                            <IconButton
                              onClick={handleSaveEdit}
                              sx={{
                                color: primaryColor,
                                '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.1)' }
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.cancel', 'Cancel')}>
                            <IconButton
                              onClick={handleCancelEdit}
                              sx={{
                                color: 'orange',
                                '&:hover': { backgroundColor: 'rgba(255, 165, 0, 0.1)' }
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#222',
                                    fontSize: '1rem',
                                    lineHeight: 1.2
                                  }}
                                >
                                  {collection.title}
                                </Typography>
                                {!!collection.autoProjectEnabled && (
                                  <Chip 
                                    size="small" 
                                    label={t('collection.auto_enabled', 'Auto')}
                                    sx={{ 
                                      height: 22, 
                                      fontSize: '0.7rem',
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontWeight: 600,
                                      border: '1px solid rgba(76, 175, 80, 0.2)'
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    lineHeight: 1.4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: 'rgba(0,0,0,0.65)'
                                  }}
                                >
                                  {t('collection.created_on', 'Created on')}: {new Date(collection.created_at).toLocaleDateString()}
                                </Typography>
                                {collection.autoProjectPath && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('collection.auto_path', 'Auto path')}: {collection.autoProjectPath}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ flex: 1, pr: 12, maxWidth: 'calc(100% - 160px)' }}
                          />
                          
                          <ListItemSecondaryAction sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 12,
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center',
                            zIndex: 3
                          }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title={t('collection.auto_create_project', 'Auto create project')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAutoCreateProject(collection)}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0.5,
                                    color: '#ff9800',
                                    backgroundColor: 'rgba(255, 152, 0, 0.06)',
                                    border: '1px solid rgba(255, 152, 0, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(255, 152, 0, 0.12)',
                                      transform: 'scale(1.04)',
                                      borderColor: '#ff9800'
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  <AutoAwesomeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={t('collection.rename_collection', 'Rename collection')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCollection(collection)}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0.5,
                                    color: '#5757b0',
                                    backgroundColor: 'rgba(87, 87, 176, 0.06)',
                                    border: '1px solid rgba(87, 87, 176, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(87, 87, 176, 0.12)',
                                      transform: 'scale(1.04)',
                                      borderColor: '#5757b0'
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title={t('collection.delete_collection', 'Delete collection')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCollection(collection)}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    p: 0.5,
                                    color: '#d32f2f',
                                    backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                    border: '1px solid rgba(211, 47, 47, 0.12)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.12)',
                                      transform: 'scale(1.04)',
                                      borderColor: '#d32f2f'
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 18 },
                                    transition: 'all 0.15s ease'
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                    {index < collections.length - 1 && (
                      <Divider sx={{ borderColor: 'rgba(87, 87, 176, 0.08)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Fade>

      {/* Refresh Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <FolderSpecialIcon />}
          onClick={loadCollections}
          disabled={loading}
          sx={{
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: hoverColor,
              backgroundColor: `${primaryColor}10`
            }
          }}
        >
          {t('collection.refresh_collections', 'Refresh Collections')}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `2px solid ${primaryColor}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#d32f2f', 
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          {t('collection.delete_collection_title', 'Delete Collection')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('collection.delete_collection_warning', 'Are you sure you want to delete this collection?')}
          </Typography>
          {collectionToDelete && (
            <Paper
              sx={{ 
                p: 2, 
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2',
                borderRadius: 2
              }}
            >
              <Typography variant="h6" sx={{ color: '#d32f2f', mb: 1 }}>
                {collectionToDelete.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('collection.delete_collection_note', 'This action cannot be undone. Projects in this collection will not be deleted, but will no longer be grouped together.')}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={confirmDeleteCollection}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#c62828' }
            }}
            startIcon={<DeleteIcon />}
          >
            {t('collection.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto Add Project Dialog */}
      <AutoAddProjectDialog
        ref={autoAddDialogRef}
        open={autoAddDialogOpen}
        onClose={() => setAutoAddDialogOpen(false)}
        collection={selectedCollection}
        templates={templates}
        templatesLoading={templatesLoading}
        onSave={(config) => {
          sendCommand('saveAutoAddProjectConfig', config);
        }}
        onLoadTemplates={() => {
          setTemplatesLoading(true);
          sendCommand('getAllTemplates', {});
        }}
        onLoadConfig={(collectionId) => {
          sendCommand('getAutoAddProjectConfig', { collectionId });
        }}
        folderPicker={{ handleBrowseDirectory }}
      />

      {/* Folder Browser Component */}
      {FolderBrowserComponent}
    </Box>
  );
});

CollectionSettings.displayName = 'CollectionSettings';
export default CollectionSettings;
